// ProductTableWizard.jsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useTable } from "../../context/TableContext.js";
import sampleBillingData from "../../Samples/BillingSample.js";
import samplePurchaseCreditNote from "../../Samples/DebitNote.js";
import sampleDeliveryNote from "../../Samples/DeliveryNote.js";
import sampleGRNData from "../../Samples/GRNData.js";
import samplePurchaseInvoice from "../../Samples/Purchase_invoice.js";
import samplePurchaseOrder from "../../Samples/PurchaseOrder.js";
import samplePurchaseReturn from "../../Samples/PurchaseReturn.js";
import sampleSalesOrder from "../../Samples/SaleOrder.js";
import SalesInvoice from '../../Samples/SalesInvoice.js';
import SalesReturn from '../../Samples/SalesReturn.js';
import sampleCreditNote from "../../Samples/CreditNote.js";

// prettify key -> label
const toTitle = (s) =>
  String(s)
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

export default function ProductTableWizard() {
  const { state, actions } = useTable();
  const visible = state.showProductTableWizard;

  /* ----------------------- Build OPTIONS from samples ---------------------- */
  const OPTIONS = useMemo(() => {
    const items =
  state.type === 'billing_invoice'
    ? sampleBillingData?.InvoiceArray_sample?.[0]?.itemData ?? []
  : state.type === 'sales_order'
    ? sampleSalesOrder?.SaleOrder_sample?.[0]?.itemData ?? []
  : state.type === 'delivery_note'
    ? sampleDeliveryNote?.DeliveryNote_sample?.[0]?.itemData ?? []
  : state.type === 'sales_invoice'
    ? SalesInvoice?.SaleInvoice_sample?.[0]?.itemData ?? []
  : state.type === 'sales_return'
    ? SalesReturn?.SalesReturn_sample?.[0]?.itemData ?? []
  : state.type === 'sales_credit_note'
    ? sampleCreditNote?.CreditNoteData?.[0]?.itemData ?? []
  : state.type === 'purchase_order'
    ? samplePurchaseOrder?.PurchaseOrder_sample?.[0]?.itemData ?? []
  : state.type === 'good_receipt_note'
    ? sampleGRNData?.GRNData_1?.[0]?.itemData ?? []
  : state.type === 'purchase_invoice'
    ? samplePurchaseInvoice?.PurchaseInvoice_sample?.[0]?.itemData ?? []
  : state.type === 'purchase_return'
    ? samplePurchaseReturn?.PurchaseReturn_sample?.[0]?.itemData ?? []
  : state.type === 'purchase_credit_note'
    ? samplePurchaseCreditNote?.DebitNote_Sample?.[0]?.itemData ?? []
  : [];


    const firstItem = items?.[0] ?? {};
    const batchData =
      firstItem?.itemBatchData?.[0] ?? firstItem?.batchData?.[0] ?? {};

    const keys = Array.from(
      new Set([...Object.keys(firstItem), ...Object.keys(batchData)])
    );

    const fromItems = keys.map((k) => ({ id: k, label: toTitle(k) }));
    return [{ id: "sno", label: "S.No" }, ...fromItems];
  }, [state.type]);

  /* ----------------------------- Local state ------------------------------ */
  const [selected, setSelected] = useState([]);     // array<string>
  const [widths, setWidths] = useState({});         // { [id]: number }
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");

  /* -------------------------- Helpers / utilities ------------------------- */
  const distribute = useCallback((ids) => {
    const n = ids.length || 1;
    const eq = +(100 / n).toFixed(2);
    const w = {};
    ids.forEach((id, i) => {
      w[id] = i === n - 1 ? +(100 - eq * (n - 1)).toFixed(2) : eq;
    });
    return w;
  }, []);

  const equalize = useCallback(() => {
    setWidths((prev) => distribute(selected));
  }, [selected, distribute]);

  const total = useMemo(
    () => selected.reduce((sum, id) => sum + (Number(widths[id]) || 0), 0),
    [selected, widths]
  );
  const valid = total >= 99 && total <= 100 && selected.length > 0;

  /* ----------------------------- Defaults open ---------------------------- */
  useEffect(() => {
    if (!visible) return;

    const preferred = [
      "sno",
      "item_name",
      "item_quantity",
      "item_unit_price",
      "item_total_amount",
    ];
    const available = new Set(OPTIONS.map((o) => o.id));
    let next = preferred.filter((id) => available.has(id));

    if (next.length === 0) {
      const real = OPTIONS.filter((o) => o.id !== "sno")
        .slice(0, 5)
        .map((o) => o.id);
      next = real.length ? real : ["sno"];
    }

    setSelected(next);
    setWidths(distribute(next));
    setError("");
    setFilter("");
  }, [visible, OPTIONS, state.type, distribute]);

  /* ------------------------------- Actions -------------------------------- */
  const handleSelectAll = () => {
    const next = OPTIONS.map((o) => o.id);
    setSelected(next);
    setWidths(distribute(next));
  };

  const handleClear = () => {
    setSelected([]);
    setWidths({});
  };

  const toggleSelect = (id) => {
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : [...selected, id];

    setSelected(next);
    setWidths(distribute(next));
    setError("");
  };

  const changeWidth = (id, val) => {
    const num = Math.max(0, Math.min(100, Number(val) || 0));
    setWidths((prev) => ({ ...prev, [id]: num }));
  };

  const removeSelected = (id) => {
    if (!selected.includes(id)) return;
    const next = selected.filter((x) => x !== id);
    setSelected(next);
    setWidths(distribute(next));
  };

  const move = (id, dir) => {
    const idx = selected.indexOf(id);
    if (idx < 0) return;
    const swapWith = idx + dir;
    if (swapWith < 0 || swapWith >= selected.length) return;
    const next = [...selected];
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    setSelected(next);
    // keep widths as-is when just reordering
  };

  const submit = () => {
    if (!valid) {
      setError(
        "Total width must be between 99% and 100%, and at least one column must be selected."
      );
      return;
    }
    const columns = selected.map((id) => {
      const opt = OPTIONS.find((o) => o.id === id);
      return {
        col_name: opt?.label || id,
        width: `${(Number(widths[id]) || 0).toFixed(2)}%`,
      };
    });
    actions.submitProductTableWizard(columns);
  };

  if (!visible) return null;

  /* ------------------------------ Rendering ------------------------------- */
  const filteredOptions = OPTIONS.filter((o) =>
    o.label.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-auto"
      onClick={(e) =>
        e.target === e.currentTarget && actions.closeProductTableWizard()
      }
      role="dialog"
      aria-modal="true"
      aria-labelledby="ptw-title"
    >
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-EAECF0 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-EAECF0 flex items-center justify-between">
          <h3 id="ptw-title" className="text-xlg font-semibold text-000000">
            Configure Product Table
          </h3>
          <button
            onClick={actions.closeProductTableWizard}
            className="w-8 h-8 grid place-items-center rounded-md text-secondary hover:bg-f3f8ff focus:outline-none focus:ring-2 focus:ring-focus-color"
            aria-label="Close"
            type="button"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* LEFT: Select columns */}
          <div className="rounded-xl border border-EAECF0">
            <div className="px-4 py-3 border-b border-EAECF0 flex items-center gap-2">
              <h4 className="font-medium text-000000">Select Columns</h4>
              <span className="ml-auto text-xs text-667085">
                {selected.length} selected
              </span>
            </div>

            <div className="p-4 space-y-3">
              {/* Search + actions */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search columns…"
                  className="flex-1 rounded-md border border-EAECF0 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-focus-color"
                />
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="px-3 py-2 rounded-md border border-EAECF0 text-secondary text-sm hover:bg-f3f8ff"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-3 py-2 rounded-md border border-EAECF0 text-secondary text-sm hover:bg-f3f8ff"
                >
                  Clear
                </button>
              </div>

              {/* Checkbox list — left aligned, no centering */}
              <div className="h-full overflow-auto pr-1 max-h-[75vh] overflow-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 place-items-start content-start">
                  {filteredOptions.map((opt) => (
                    <label
                      key={opt.id}
                      className="flex items-start justify-start gap-2 text-sm text-left"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 shrink-0"
                        checked={selected.includes(opt.id)}
                        onChange={() => toggleSelect(opt.id)}
                      />
                      <span className="text-secondary text-left leading-tight break-words min-w-0">
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: Widths editor */}
          <div className="rounded-xl border border-EAECF0">
            <div className="px-4 py-3 border-b border-EAECF0 flex items-center gap-2">
              <h4 className="font-medium text-000000">Column Widths (%)</h4>
              <button
                type="button"
                onClick={equalize}
                className="ml-auto px-3 py-1.5 rounded-md bg-D5E6Ff text-277DFE text-sm hover:bg-f3f8ff"
              >
                Equalize
              </button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto max-h-[75vh]">
              {selected.length === 0 ? (
                <p className="text-sm text-667085">No columns selected.</p>
              ) : (
                selected.map((id) => {
                  const label = OPTIONS.find((o) => o.id === id)?.label || id;
                  const v = widths[id] ?? 0;
                  return (
                    <div key={id} className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-40 truncate text-sm text-000000">
                          {label}
                        </div>

                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={0.1}
                          value={v}
                          onChange={(e) => changeWidth(id, e.target.value)}
                          className="w-24 rounded-md border border-EAECF0 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-focus-color"
                        />
                        <span className="text-667085 text-sm">%</span>

                        {/* reorder + remove */}
                        <div className="ml-auto flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => move(id, -1)}
                            className="w-8 h-8 grid place-items-center rounded-md border border-EAECF0 text-secondary hover:bg-f3f8ff"
                            aria-label="Move up"
                          >
                            ↑
                          </button>
                          <button
                            type="button"
                            onClick={() => move(id, +1)}
                            className="w-8 h-8 grid place-items-center rounded-md border border-EAECF0 text-secondary hover:bg-f3f8ff"
                            aria-label="Move down"
                          >
                            ↓
                          </button>
                          <button
                            type="button"
                            onClick={() => removeSelected(id)}
                            className="w-8 h-8 grid place-items-center rounded-md border border-EAECF0 text-F51D00 hover:bg-rose-50"
                            aria-label="Remove"
                          >
                            ×
                          </button>
                        </div>
                      </div>

                      {/* slider */}
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={0.1}
                        value={v}
                        onChange={(e) => changeWidth(id, e.target.value)}
                        className="w-full accent-277DFE"
                      />
                    </div>
                  );
                })
              )}

              {/* total bar */}
              <div className="mt-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-667085">Total</span>
                  <span
                    className={`font-semibold ${
                      valid ? "text-027A48" : "text-F51D00"
                    }`}
                  >
                    {total.toFixed(2)}%
                  </span>
                </div>
                <div className="mt-1 h-2 w-full rounded-full bg-f3f4f6 overflow-hidden border border-EAECF0">
                  <div
                    className={`h-full ${
                      valid ? "bg-027A48" : "bg-F51D00"
                    }`}
                    style={{ width: `${Math.min(100, Math.max(0, total))}%` }}
                  />
                </div>
                <p className="mt-1 text-xs text-667085">
                  Target 99% – 100% for best fit.
                </p>

                {error && <p className="mt-2 text-sm text-F51D00">{error}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-EAECF0 flex items-center justify-end gap-3">
          <button
            onClick={actions.closeProductTableWizard}
            type="button"
            className="px-4 py-2 rounded-md border border-EAECF0 text-secondary hover:bg-f3f8ff focus:outline-none focus:ring-2 focus:ring-focus-color"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!valid}
            type="button"
            className={`px-4 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-focus-color ${
              valid
                ? "bg-reports-shade6_text hover:bg-reports-shade6_bg text-reports-shade6_bg hover:text-reports-shade6_text"
                : "bg-disabled-color text-F4F4F4 cursor-not-allowed"
            }`}
          >
            Add Table
          </button>
        </div>
      </div>
    </div>
  );
}
